/* ==========================================================================
   scroll-spin.js — scroll-driven 3D product carousel
   --------------------------------------------------------------------------
   Vanilla JS, no dependencies, no build step. Safe to drop into a Shopify
   theme's assets/ folder and include with `script_tag` (or defer).

   Markup contract (see scroll-spin.html / scroll-spin.liquid):

     <div class="scroll-spin" data-turns="1.5" data-drag="true">
       <div class="scroll-spin__stage">
         <div class="scroll-spin__scene">
           <div class="scroll-spin__ring">
             <div class="scroll-spin__item" data-label="Mint"><img ...></div>
             ...
           </div>
         </div>
         <div class="scroll-spin__caption"></div>
       </div>
     </div>

   Data attributes on .scroll-spin (all optional):
     data-turns      number  full rotations across the scroll track   (1.5)
     data-spacing    number  ring radius multiplier, >1 = roomier     (1.0)
     data-ease       0..1    smoothing; lower = floatier              (0.12)
     data-direction  1|-1    spin direction                           (1)
     data-drag       bool    allow click/touch drag to spin           (true)
     data-start      number  starting rotation offset in degrees      (0)
   ========================================================================== */

(function () {
  'use strict';

  var instances = [];
  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

  // Some themes scroll an inner wrapper instead of the document itself above
  // a layout breakpoint — Shopify's own "Horizon" theme does this above
  // 990px, keeping `document.scrollingElement` fixed so only its
  // `.page-wrapper` scrolls. `window`/`document` never fire a 'scroll' event
  // in that case, so the actual scrolling ancestor has to be detected at
  // runtime rather than assumed to always be the window.
  function findScrollParent(el) {
    var node = el.parentElement;
    while (node && node !== document.body) {
      var oy = window.getComputedStyle(node).overflowY;
      if (oy === 'auto' || oy === 'scroll' || oy === 'overlay') return node;
      node = node.parentElement;
    }
    return null;
  }

  function num(el, attr, fallback) {
    var v = parseFloat(el.getAttribute(attr));
    return isNaN(v) ? fallback : v;
  }

  function clamp(v, min, max) {
    return v < min ? min : v > max ? max : v;
  }

  /* Shortest signed distance between two angles, in degrees (-180..180]. */
  function angleDelta(deg) {
    var a = ((deg % 360) + 540) % 360 - 180;
    return a;
  }

  function ScrollSpin(root) {
    this.root = root;
    this.ring = root.querySelector('.scroll-spin__ring');
    this.scene = root.querySelector('.scroll-spin__scene');
    this.items = Array.prototype.slice.call(root.querySelectorAll('.scroll-spin__item'));
    this.caption = root.querySelector('.scroll-spin__caption');
    this.dots = Array.prototype.slice.call(root.querySelectorAll('.scroll-spin__dot'));

    if (!this.ring || this.items.length === 0) return;

    this.turns = num(root, 'data-turns', 1.5);
    this.spacing = num(root, 'data-spacing', 1);
    this.ease = clamp(num(root, 'data-ease', 0.12), 0.01, 1);
    this.dir = num(root, 'data-direction', 1) < 0 ? -1 : 1;
    this.start = num(root, 'data-start', 0);
    this.dragEnabled = root.getAttribute('data-drag') !== 'false';
    // How scroll maps to rotation: 'both' follows the scrollbar exactly,
    // 'hold' never rewinds, 'forward' always turns the same way.
    this.spinMode = root.getAttribute('data-spin-mode') || 'both';
    this.maxProgress = 0;      // furthest point reached, for 'hold'
    this.travelled = 0;        // total distance scrolled, for 'forward'
    this.lastProgress = null;  // set on the first read, not assumed to be 0

    // Thickness as a fraction of the image width, so it holds up at any size.
    this.depthRatio = clamp(num(root, 'data-depth', 0.055), 0, 0.2);
    // Every layer is a separately composited *masked* box inside a 3D
    // transform — one of the most expensive combinations a browser can
    // render, and it doesn't scale gracefully with element count. Kept
    // deliberately low: past this it costs frames for no visible gain, and
    // with several products in the ring at once the per-layer cost adds up
    // fast (see the occlusion cull in render(), which is the other half of
    // keeping this affordable).
    this.layers = this.depthRatio > 0
      ? Math.round(clamp(this.depthRatio * 90, 3, 6))
      : 0;

    this.step = 360 / this.items.length;
    this.rot = this.start;        // rendered rotation
    this.target = this.start;     // rotation we're easing toward
    this.dragOffset = 0;          // extra rotation contributed by dragging
    this.dragging = false;        // a drag gesture is in progress right now
    this.flick = 0;               // leftover drag momentum, deg per 60Hz frame
    this.frontIndex = -1;
    this.visible = false;
    this.rafId = null;
    this.idleFrames = 0;
    this.scrollDirty = true;      // a scroll happened; re-read geometry in tick
    this.lastTime = 0;

    // Last values written per item, so a frame that changes nothing writes
    // nothing. Style writes are the expensive part of spinning 72 elements.
    this.lastOpacity = [];
    this.lastBright = [];
    this.lastZ = [];
    this.hidden = [];
    this.slabHidden = [];
    this.parts = [];

    this.onScroll = this.onScroll.bind(this);
    this.onResize = this.onResize.bind(this);
    this.tick = this.tick.bind(this);

    this.build();
    this.bind();
    this.measure();
    this.readScroll();
    this.rot = this.target;       // no fly-in on first paint
    this.render();

    root.setAttribute('data-ss-ready', '');

    // --- settle-measure guard -------------------------------------------
    // The very first measure() above can run before scroll-spin.css has
    // actually been applied (a deferred script can execute while its own
    // <link rel="stylesheet"> is still resolving, and Shopify's asset CDN
    // adds its own latency on top). When that happens, `.scroll-spin__ring`
    // hasn't received its `width: var(--ss-cw)` rule yet, so
    // getComputedStyle() falls through to some other box's width instead —
    // in testing this reliably grabbed the width of `.scroll-spin__scene`
    // (the ring's containing block), which is several times too large.
    // That bad radius then gets stamped onto --ss-radius and never gets
    // revisited: the ResizeObserver below only fires again if `root`'s own
    // box changes size, and a mobile page load never does that. The result
    // is a permanently oversized ring — cards land far outside the visible
    // stage, and what should look like a tight, three-dimensional turntable
    // instead looks broken and flat.
    //
    // A second measurement one paint frame later, plus one more once every
    // asset on the page (stylesheet included) has actually finished
    // loading, catches this without needing to know exactly why the first
    // one was wrong.
    var self = this;
    requestAnimationFrame(function () {
      self.measure();
      self.render();
    });
    if (document.readyState !== 'complete') {
      window.addEventListener('load', function () {
        self.measure();
        self.render();
      }, { once: true });
    }
  }

  ScrollSpin.prototype.build = function () {
    var self = this;
    this.items.forEach(function (item, i) {
      item.style.setProperty('--ss-angle', (i * self.step).toFixed(4) + 'deg');

      self.parts[i] = { photo: null, layers: [], slab: null };
      self.lastOpacity[i] = -1;
      self.lastBright[i] = -1;
      self.lastZ[i] = -1;
      self.hidden[i] = false;
      self.slabHidden[i] = null;

      var img = item.querySelector('img');
      if (img) {
        img.setAttribute('draggable', 'false');
        // First two frames load eagerly so the section is never empty on entry.
        if (!img.getAttribute('loading')) {
          img.setAttribute('loading', i < 2 ? 'eager' : 'lazy');
        }
        img.setAttribute('decoding', 'async');
        self.parts[i].photo = img;
        self.extrude(item, img, i);
      }
    });
  };

  /* --- give a flat image some thickness -----------------------------------
     Stacks masked copies of the image behind the photo, filled with the
     product's own colour, so the silhouette becomes a solid object with a
     matching back panel instead of a sheet of paper.                       */
  ScrollSpin.prototype.extrude = function (item, img, index) {
    var self = this;
    if (this.layers < 1) return;

    var slab = document.createElement('div');
    slab.className = 'scroll-spin__slab';
    slab.setAttribute('aria-hidden', 'true');

    for (var i = 1; i <= this.layers; i++) {
      var t = i / this.layers;                 // 0 = at the photo, 1 = the back
      var layer = document.createElement('span');
      layer.className = 'scroll-spin__layer';
      layer.style.setProperty('--ss-i', t.toFixed(4));
      // Front edge stays near the body colour; the back panel sits in shade.
      // Fixed once here rather than recomputed per frame: a changing filter
      // forces the layer to re-rasterise, a changing opacity doesn't.
      layer.style.setProperty('--ss-shade', (0.94 - t * 0.26).toFixed(3));
      slab.appendChild(layer);
      this.parts[index].layers.push(layer);
    }

    this.parts[index].slab = slab;
    item.insertBefore(slab, item.firstChild);

    // A colour the merchant set on the block needs no pixels, so it applies
    // straight away rather than waiting on the image.
    var preset = item.getAttribute('data-color');
    if (preset) item.style.setProperty('--ss-body', preset);

    function ready() {
      var src = img.currentSrc || img.src;
      if (!src) return;

      // Put the back panel on screen NOW, masked straight off the product
      // image's own URL. This used to wait for analyse() below to call back
      // before marking the slab masked — and an unmasked slab is display:none
      // in the CSS, so if that step didn't finish the card had no back at all
      // and vanished the moment it turned past 90°.
      //
      // That mattered because analyse() is not a safe thing to depend on: it
      // re-requests the image through a second, CORS-mode fetch (needed to
      // read pixels out of a canvas) that misses the cache the visible image
      // just filled, and on a phone over mobile data that request is slow and
      // can fail outright. mask-image needs neither CORS nor a canvas, so
      // masking off the URL directly always works. These product images are
      // only 210px wide, so masking the original costs nothing.
      item.style.setProperty('--ss-mask', 'url("' + src + '")');
      slab.setAttribute('data-ss-masked', '');

      // Everything analyse() provides is now an upgrade to something already
      // visible — a cheaper downscaled mask, and the product's real colour
      // in place of the CSS default — so failing costs appearance, not the
      // whole effect.
      self.analyse(src, function (out) {
        if (out.mask) item.style.setProperty('--ss-mask', 'url("' + out.mask + '")');
        if (!preset && out.colour) item.style.setProperty('--ss-body', out.colour);
      });
    }

    if (img.complete && img.naturalWidth) ready();
    else img.addEventListener('load', ready, { once: true });
  };

  /* One pass over the image that produces both things the slab needs:

     colour — sampled just inside the left and right edges of the silhouette,
       row by row. That strip is the outer shell of the product, so it dodges
       the screen, the click wheel and any other artwork in the middle. The
       median of those pixels is what the sides and back get painted.

     mask — a downscaled copy of the image, used for its alpha channel only.
       Cheap to rasterise, and the softened edge actually helps the extrusion
       sit against the photo.

     Falls back silently: a cross-origin image that refuses to be read keeps
     the CSS default colour and masks off the original file instead. */
  ScrollSpin.prototype.analyse = function (src, done) {
    var probe = new Image();
    probe.crossOrigin = 'anonymous';

    probe.onerror = function () { done({}); };

    probe.onload = function () {
      var out = {};
      try {
        var S = 96;
        var canvas = document.createElement('canvas');
        canvas.width = canvas.height = S;
        var ctx = canvas.getContext('2d', { willReadFrequently: true });
        ctx.drawImage(probe, 0, 0, S, S);

        var data = ctx.getImageData(0, 0, S, S).data;
        var rs = [], gs = [], bs = [];
        var inset = 3;   // step past the antialiased rim

        var at = function (x, y) { return (y * S + x) * 4; };
        var opaque = function (x, y) { return data[at(x, y) + 3] > 200; };

        var push = function (x, y) {
          if (x < 0 || x >= S) return;
          var p = at(x, y);
          if (data[p + 3] < 200) return;
          if (data[p] + data[p + 1] + data[p + 2] < 90) return;   // shadow/black
          rs.push(data[p]); gs.push(data[p + 1]); bs.push(data[p + 2]);
        };

        // Skip the top and bottom few rows, where a rounded corner would make
        // the "edge" pixel land on the corner radius instead of the side wall.
        for (var y = Math.round(S * 0.15); y < Math.round(S * 0.95); y++) {
          var left = -1, right = -1;
          for (var x = 0; x < S; x++) {
            if (opaque(x, y)) { left = x; break; }
          }
          for (var x2 = S - 1; x2 >= 0; x2--) {
            if (opaque(x2, y)) { right = x2; break; }
          }
          if (left < 0 || right - left < 8) continue;
          push(left + inset, y);
          push(right - inset, y);
        }

        if (rs.length >= 12) {
          var mid = function (arr) {
            arr.sort(function (a, b) { return a - b; });
            return arr[Math.floor(arr.length / 2)];
          };
          out.colour = 'rgb(' + mid(rs) + ',' + mid(gs) + ',' + mid(bs) + ')';
        }

        // Redraw at mask resolution, preserving the source's aspect ratio so
        // `mask-size: contain` lines the silhouette up with the photo exactly.
        var M = 220;
        var mw = probe.naturalWidth || M;
        var mh = probe.naturalHeight || M;
        var k = M / Math.max(mw, mh);
        var mask = document.createElement('canvas');
        mask.width = Math.max(1, Math.round(mw * k));
        mask.height = Math.max(1, Math.round(mh * k));
        mask.getContext('2d').drawImage(probe, 0, 0, mask.width, mask.height);
        out.mask = mask.toDataURL('image/png');
      } catch (err) {
        /* tainted canvas — fall back to the defaults in `done` */
      }
      done(out);
    };

    probe.src = src;
  };

  ScrollSpin.prototype.measure = function () {
    // Radius that seats N cards of width W evenly around a circle,
    // then a spacing multiplier so they don't graze each other.
    // Computed style, not getBoundingClientRect: the ring is already inside a
    // perspective and rect widths come back foreshortened.
    var w = parseFloat(window.getComputedStyle(this.ring).width) || 260;
    var n = Math.max(this.items.length, 3);
    var radius = (w / 2) / Math.tan(Math.PI / n) * this.spacing;
    radius = Math.max(radius, w * 0.9);
    this.radius = radius;
    this.root.style.setProperty('--ss-radius', radius.toFixed(2) + 'px');
    this.root.style.setProperty('--ss-depth', (w * this.depthRatio).toFixed(2) + 'px');

    // Perspective is set HERE, as a plain pixel value, not left to the
    // stylesheet. Two reasons:
    //
    // 1. iOS Safari refuses computed values (calc()/var() chains) in the
    //    `perspective` property — it silently treats them as invalid, which
    //    resolves to `perspective: none`. With no perspective there is no
    //    foreshortening at all: side cards render full height, front-facing,
    //    and the same size as the front card, which reads as a flat,
    //    "inside-out" shuffle instead of a turning ring. Chrome resolves the
    //    same CSS fine, which is why the bug only shows up on phones.
    //
    // 2. What makes the rotation read as 3D is the RATIO of the ring's
    //    radius to the camera distance. Deriving the distance from the
    //    radius we just computed locks that ratio at every size and every
    //    card-width setting, with no CSS breakpoint able to drift it.
    //
    // Smaller screens use a closer camera (lower multiplier): at phone size
    // the whole ring subtends a narrow angle, so the same ratio that looks
    // right on a desktop looks weak — pulling the camera in restores the
    // keystone/depth cues that sell the turn.
    var perspFactor = window.innerWidth < 750 ? 2.4 : 4.28;
    if (this.scene) {
      this.scene.style.perspective = Math.round(radius * perspFactor) + 'px';
    }
  };

  ScrollSpin.prototype.bind = function () {
    var self = this;

    this.scrollTarget = findScrollParent(this.root) || window;
    this.scrollTarget.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onResize, { passive: true });

    if ('ResizeObserver' in window) {
      this.ro = new ResizeObserver(function () { self.onResize(); });
      this.ro.observe(this.root);
      // The ring itself is what actually needs to stay in sync with
      // --ss-scale — its width can change (a breakpoint flips, a font
      // finishes loading and reflows things) without `root`'s own box
      // changing size at all, in which case the observer above never
      // fires. Watching the ring too closes that gap.
      this.ro.observe(this.ring);
    }

    // Only animate while the section is anywhere near the viewport.
    if ('IntersectionObserver' in window) {
      this.io = new IntersectionObserver(function (entries) {
        self.visible = entries[0].isIntersecting;
        if (self.visible) { self.preload(); self.wake(); }
      }, { rootMargin: '100px 0px' });
      this.io.observe(this.root);
    } else {
      this.visible = true;
      this.preload();
    }

    if (this.dragEnabled && window.PointerEvent) this.bindDrag();
  };

  /* --- drag to spin -------------------------------------------------------
     Horizontal drags spin the ring; vertical gestures are left alone so the
     page still scrolls normally on touch devices.                          */
  ScrollSpin.prototype.bindDrag = function () {
    var self = this;
    var scene = this.root.querySelector('.scroll-spin__scene');
    if (!scene) return;

    var active = false, decided = false, startX = 0, startY = 0, lastX = 0, pid = null;
    var velocity = 0;

    scene.style.touchAction = 'pan-y';
    scene.style.cursor = 'grab';

    /* How much spin one pixel of drag is worth. Derived rather than fixed:
       the goal is that one comfortable swipe advances about one product, and
       "one comfortable swipe" is a fraction of the screen's width, not an
       absolute pixel count. A flat 0.25°/px (what this used to be) needs
       240px of travel to bring up the next colour — most of the width of a
       phone — so on mobile the ring barely budged for a normal thumb swipe
       and the whole thing felt stuck. This lands at ~0.5°/px on a phone and
       leaves the desktop feel unchanged. */
    function degPerPx() {
      var span = Math.max(90, Math.min(window.innerWidth * 0.3, 240));
      return self.step / span;
    }

    scene.addEventListener('pointerdown', function (e) {
      if (e.button != null && e.button !== 0) return;
      if (e.isPrimary === false) return;
      self.flick = 0;             // grabbing it stops any glide in progress
      velocity = 0;
      active = true;
      decided = false;
      pid = e.pointerId;
      startX = lastX = e.clientX;
      startY = e.clientY;
      // Captured up front, not on the frame the gesture is recognised: a
      // finger that slides off the scene's box mid-swipe (easy to do — it's
      // only as tall as the images) otherwise stops delivering pointermove
      // and the spin dies halfway through. A real interruption still
      // arrives as pointercancel.
      if (scene.setPointerCapture) {
        try { scene.setPointerCapture(pid); } catch (err) { /* not capturable */ }
      }
    });

    scene.addEventListener('pointermove', function (e) {
      if (!active || e.pointerId !== pid) return;

      // No button held any more (a pointerup we never saw, or a stray hover
      // event): bail out rather than spinning on an imaginary drag. Mouse
      // only — a touch pointer's `buttons` can read 0 mid-gesture on some
      // mobile browsers, which killed the drag on the first move event.
      if (e.pointerType === 'mouse' && e.buttons === 0) { end(e); return; }

      if (!decided) {
        var dx = Math.abs(e.clientX - startX);
        var dy = Math.abs(e.clientY - startY);
        // A fingertip covers ground less precisely than a cursor, so it
        // needs a shorter runway before the gesture is taken seriously.
        var trigger = e.pointerType === 'mouse' ? 6 : 3;
        if (dx < trigger && dy < trigger) return;
        // Fingers almost never travel perfectly horizontally. The old test
        // was `dy > dx` — bail if there's ANY more vertical than horizontal
        // movement — and it bailed by setting `active = false`, which was
        // permanent: a swipe that started 20px across and 21px down was
        // written off as a page scroll and could never recover, even as it
        // went on to travel 200px sideways. Now only a decisively vertical
        // move gives up, and anything ambiguous keeps watching instead of
        // throwing the gesture away.
        if (dy > dx * 1.4) { active = false; return; }   // a page scroll
        if (dx <= dy) return;                            // still ambiguous
        decided = true;
        self.dragging = true;
        scene.style.cursor = 'grabbing';
        // Start measuring spin from here, so the travel spent deciding
        // doesn't get applied in one jump.
        lastX = e.clientX;
      }

      // Big jumps are clamped so a dropped frame (or a synthetic event)
      // can't fling the ring across the room.
      var dxStep = clamp(e.clientX - lastX, -80, 80);
      var spin = dxStep * degPerPx();
      // Unbounded on purpose — see rebase(). This used to be clamped to
      // ±720°, i.e. two turns, after which dragging the same way did
      // literally nothing: the ring stopped dead mid-gesture and only
      // dragging back the other way revived it. rebase() folds full turns
      // away every frame instead, so you can keep spinning forever.
      self.dragOffset += spin;
      // Rolling average of recent movement, used as the release velocity.
      velocity = velocity * 0.7 + spin * 0.3;
      lastX = e.clientX;
      self.wake();
    });

    function end(e) {
      if (e && pid != null && e.pointerId !== pid) return;
      // Release the capture even for a gesture that was handed back to the
      // page as a scroll (which sets active = false without ending). That
      // used to return early and leave the pointer captured on a dead id.
      if (pid != null && scene.releasePointerCapture) {
        try { scene.releasePointerCapture(pid); } catch (err) { /* already gone */ }
      }
      pid = null;
      if (!active) { self.dragging = false; return; }
      // Let go mid-swipe and the ring glides to a stop instead of stopping dead.
      if (decided) self.flick = clamp(velocity, -14, 14);
      velocity = 0;
      active = false;
      decided = false;
      self.dragging = false;
      scene.style.cursor = 'grab';
      self.wake();                // let the glide start immediately
    }

    scene.addEventListener('pointerup', end);
    scene.addEventListener('pointercancel', end);
    // pointerleave is a mouse concern only. A finger that strays outside the
    // scene box is still mid-swipe, and ending the drag there cut plenty of
    // real gestures short; pointercancel covers the genuine interruptions.
    scene.addEventListener('pointerleave', function (e) {
      if (e.pointerType === 'mouse') end(e);
    });
    // Releases that happen outside the scene still have to end the drag.
    window.addEventListener('pointerup', end, true);
    window.addEventListener('blur', function () { end(); });
  };

  /* --- scroll → target rotation ------------------------------------------
     The scroll handler deliberately does no geometry: reading a rect inside a
     scroll event forces a synchronous layout on the same thread that's trying
     to scroll. It just flags the change, and the next frame reads it once. */
  ScrollSpin.prototype.onScroll = function () {
    this.scrollDirty = true;
    this.wake();
  };

  ScrollSpin.prototype.readScroll = function () {
    var rect = this.root.getBoundingClientRect();
    var travel = rect.height - window.innerHeight;
    var progress = travel > 0 ? clamp(-rect.top / travel, 0, 1) : 0;
    this.progress = progress;

    if (this.lastProgress === null) this.lastProgress = progress;

    // Clear the accumulated state only once the section has left the screen
    // downward — i.e. you've scrolled above it and it sits below the fold.
    //
    // This deliberately does NOT key off progress hitting 0. Progress is 0
    // as soon as the section's top meets the viewport's top, and at that
    // moment the section still fills the screen. Resetting there dropped the
    // rotation from a full turn to zero in one frame, so scrolling up made
    // the ring whip backwards through an entire revolution right in front of
    // the viewer. Waiting until it's genuinely off-screen means the reset is
    // never seen, and the next pass down still replays from the start.
    if (rect.top > window.innerHeight) {
      this.maxProgress = 0;
      this.travelled = 0;
      this.lastProgress = progress;
    }

    var drive;
    if (this.spinMode === 'hold') {
      // Never rewind: drive from the furthest point reached.
      if (progress > this.maxProgress) this.maxProgress = progress;
      drive = this.maxProgress;
    } else if (this.spinMode === 'forward') {
      // Always turn the same way: drive from distance scrolled, ignoring
      // which direction it was scrolled in. Keeps the ring moving on the way
      // back up instead of sitting still.
      this.travelled += Math.abs(progress - this.lastProgress);
      drive = this.travelled;
    } else {
      drive = progress;
    }
    this.lastProgress = progress;

    this.target = this.start + this.dir * drive * this.turns * 360;
  };

  ScrollSpin.prototype.onResize = function () {
    // Crossing a layout breakpoint (e.g. Horizon's 990px "squeeze" layout)
    // can swap which element actually scrolls — re-detect and rebind rather
    // than staying attached to a container that stopped moving.
    var next = findScrollParent(this.root) || window;
    if (next !== this.scrollTarget) {
      this.scrollTarget.removeEventListener('scroll', this.onScroll);
      this.scrollTarget = next;
      this.scrollTarget.addEventListener('scroll', this.onScroll, { passive: true });
    }
    this.measure();
    this.onScroll();
  };

  /* --- keep the accumulated drag from running away ------------------------
     A rotation is periodic: rotateY(390deg) and rotateY(30deg) put every
     card in exactly the same place. So once the drag offset has built up a
     whole turn, that turn can be subtracted with no visible effect at all —
     PROVIDED the same amount comes off `rot` at the same instant, which
     leaves the easing gap (goal - rot) untouched and so doesn't make the
     ring lurch or spin to "catch up".

     This is what replaces the old ±720° clamp. That clamp was there to stop
     the ring drifting far from where the scroll position says it should be,
     but it did so by dead-ending the gesture: two turns in the same
     direction and dragging simply stopped working, and a fast flick could
     pin the offset against the limit and sit there while its momentum
     decayed — the ring frozen, apparently stuck, for no visible reason.
     Folding turns away keeps the numbers small forever instead.            */
  /* --- make sure the whole ring is actually loaded ------------------------
     build() marks everything past the first two frames `loading="lazy"` to
     keep them off the critical path. That was fine while cards dissolved as
     they left the front, but now every card in the ring is on screen at
     once — sides and backs included — so the whole set has to be there.

     It matters more than just a missing photo: each card's silhouette mask
     is built from its loaded image, and a slab with no mask stays
     display:none (see the CSS). An unloaded card therefore has no back
     panel at all, so it would turn away from the front and reveal nothing
     — exactly the disappearing act this change is meant to fix. Measured on
     the live section, four of six images were still unloaded (naturalWidth
     0, no currentSrc) well after load: the browser never treated them as
     approaching the viewport, because every card sits stacked at the ring's
     centre inside a 3D transform. So they get promoted explicitly as soon
     as the section is anywhere near the viewport.                          */
  ScrollSpin.prototype.preload = function () {
    if (this.preloaded) return;
    this.preloaded = true;
    for (var i = 0; i < this.items.length; i++) {
      var img = this.parts[i] && this.parts[i].photo;
      if (img && !img.complete) img.setAttribute('loading', 'eager');
    }
  };

  ScrollSpin.prototype.rebase = function () {
    var turns = Math.trunc(this.dragOffset / 360);
    if (turns) {
      var shift = turns * 360;
      this.dragOffset -= shift;
      this.rot -= shift;
    }
  };

  ScrollSpin.prototype.wake = function () {
    this.idleFrames = 0;
    if (this.rafId == null) {
      this.lastTime = 0;          // don't ease against the whole idle gap
      this.rafId = requestAnimationFrame(this.tick);
    }
  };

  ScrollSpin.prototype.tick = function (now) {
    // Frame time, not frame count. A fixed per-frame fraction eases twice as
    // fast on a 120Hz screen as on a 60Hz one, and lurches whenever a frame
    // is dropped; this keeps the motion identical on any refresh rate.
    var dt = this.lastTime ? clamp(now - this.lastTime, 1, 50) : 16.667;
    this.lastTime = now;
    var frames = dt / 16.667;

    if (this.scrollDirty) {
      this.readScroll();
      this.scrollDirty = false;
    }

    // Momentum left over from a drag, decaying away. The decay is gentler
    // than it was (0.94 vs 0.9 per frame) so a released swipe coasts to a
    // stop over a longer, silkier arc instead of dropping its speed
    // noticeably within a few frames.
    if (this.flick) {
      this.dragOffset += this.flick * frames;
      this.flick *= Math.pow(0.94, frames);
      if (Math.abs(this.flick) < 0.02) this.flick = 0;
    }

    // Fold away whole turns before they can pile up (see rebase()).
    this.rebase();

    var goal = this.target + this.dragOffset;
    var diff = goal - this.rot;

    // The default easing (0.12) is deliberately floaty for scroll-driven
    // motion, but under a finger that same lag reads as unresponsiveness —
    // the ring visibly trails where you've dragged to. While a drag is
    // actually in progress the ring tracks the finger much more directly,
    // then reverts to the floaty setting for the release glide.
    var ease = this.dragging ? Math.max(this.ease, 0.45) : this.ease;
    this.rot += diff * (1 - Math.pow(1 - ease, frames));

    if (Math.abs(diff) < 0.01 && !this.flick) {
      this.rot = goal;
      this.idleFrames++;
    } else {
      this.idleFrames = 0;
    }

    this.render();

    // Park the loop once we've settled (and keep it parked while off-screen).
    if (this.idleFrames > 2 || !this.visible) {
      this.rafId = null;
    } else {
      this.rafId = requestAnimationFrame(this.tick);
    }
  };

  ScrollSpin.prototype.render = function () {
    // Written straight to `transform`, not through a custom property. A
    // custom property on the ring is inherited, so changing it invalidates
    // the style of every descendant — all 72 of them — on every frame.
    // `transform` doesn't inherit, so only the ring itself is touched.
    this.ring.style.transform =
      'translateZ(' + (-this.radius).toFixed(2) + 'px) rotateY(' + this.rot.toFixed(2) + 'deg)';

    var best = 0, bestAbs = 999;

    for (var i = 0; i < this.items.length; i++) {
      // Where this card actually faces once the ring has turned.
      var facing = angleDelta(i * this.step + this.rot);
      var abs = Math.abs(facing);

      // Full-round visibility: a card stays on screen for the whole turn
      // rather than dissolving as it leaves the front. Once it passes 90°
      // its photo drops away by itself (backface-visibility: hidden, in the
      // CSS) and what faces you is the extrusion stack — the product's own
      // colour, masked to its silhouette and shaded darker toward the back
      // panel. That IS the back of the device, and showing it is what makes
      // this read as a carousel of solid objects turning around a spindle
      // instead of a row of photos that blink out of existence.
      //
      // This replaces a fade that hit zero at 1.5 card-steps out (90° on a
      // six-product ring) and hid the card outright — the reason products
      // vanished the moment they left the front. The fade is now only a
      // gentle depth cue that bottoms out well above zero, so side and rear
      // cards stay solid. Note the layers compound: six stacked silhouettes
      // at 0.77 each composite to essentially opaque, so a rear card reads
      // as a solid back panel rather than something see-through.
      var opacity = 1 - 0.4 * Math.pow(abs / 180, 1.3);

      if (abs < bestAbs) { bestAbs = abs; best = i; }

      var item = this.items[i];
      var parts = this.parts[i];

      // The only card still culled is the one sitting almost exactly behind
      // the front one, where the front card hides it completely anyway.
      if (abs > 172) {
        if (!this.hidden[i]) {
          item.style.visibility = 'hidden';
          this.hidden[i] = true;
        }
        continue;
      }
      if (this.hidden[i]) {
        item.style.visibility = '';
        this.hidden[i] = false;
      }

      // A card facing straight-on hides its own extrusion: the layers sit
      // in the same silhouette as the photo, only pushed back in Z, so the
      // opaque front face occludes them completely — but ONLY at (almost)
      // exactly 0°. This threshold used to be 15°, which was fine with a
      // far camera but wrong with the closer mobile perspective: between
      // ~2° and 15° the side wall is clearly visible, so the card's
      // thickness visibly popped off right as it arrived at the front and
      // popped back as it left — the product went flat at the exact moment
      // the shopper was looking at it. At 1.2° the wall is genuinely
      // sub-pixel, so the switch is invisible, and the front card still
      // sheds its masked layers while the ring is at rest (which is when
      // it sits at ~0° for long stretches and the saving matters most).
      // ...and only while the ring is actually at rest. Toggling `display`
      // on six masked layers is itself a relayout-and-repaint, so doing it
      // as each card swept past the front put a small hitch into the spin
      // twice per card — twelve per full turn. Restricting it to the
      // settled state means no toggling at all during motion, while still
      // shedding the cost during the long idle stretches, which is where it
      // actually mattered.
      if (parts.slab) {
        var slabHidden = this.idleFrames > 0 && abs < 1.2;
        if (slabHidden !== this.slabHidden[i]) {
          this.slabHidden[i] = slabHidden;
          parts.slab.style.display = slabHidden ? 'none' : '';
        }
      }

      // Quantised so imperceptible changes don't cost a style write. Values
      // go on the leaves rather than the item, because opacity and filter on
      // the item would flatten the 3D slab inside it.
      var qo = Math.round(opacity * 100) / 100;
      if (qo !== this.lastOpacity[i]) {
        this.lastOpacity[i] = qo;
        var o = String(qo);
        if (parts.photo) parts.photo.style.opacity = o;
        for (var L = 0; L < parts.layers.length; L++) {
          parts.layers[L].style.opacity = o;
        }
      }

      var qb = Math.round((1 - (abs / 180) * 0.45) * 100) / 100;
      if (qb !== this.lastBright[i] && parts.photo) {
        this.lastBright[i] = qb;
        parts.photo.style.filter = 'brightness(' + qb + ')';
      }

      var z = Math.round(1000 - abs);
      if (z !== this.lastZ[i]) {
        this.lastZ[i] = z;
        item.style.zIndex = String(z);
      }
    }

    if (best !== this.frontIndex) {
      this.frontIndex = best;
      if (this.caption) {
        this.caption.textContent = this.items[best].getAttribute('data-label') || '';
      }
      for (var d = 0; d < this.dots.length; d++) {
        this.dots[d].setAttribute('data-active', d === best ? 'true' : 'false');
      }
      this.root.dispatchEvent(new CustomEvent('scrollspin:change', {
        bubbles: true,
        detail: { index: best, item: this.items[best] }
      }));
    }
  };

  ScrollSpin.prototype.destroy = function () {
    this.scrollTarget.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onResize);
    if (this.io) this.io.disconnect();
    if (this.ro) this.ro.disconnect();
    if (this.rafId != null) cancelAnimationFrame(this.rafId);
    this.rafId = null;
    this.root.removeAttribute('data-ss-ready');
  };

  /* --- lifecycle ---------------------------------------------------------- */

  function init(scope) {
    if (REDUCED.matches) return;   // CSS already falls back to a static grid
    var roots = (scope || document).querySelectorAll('.scroll-spin:not([data-ss-ready])');
    Array.prototype.forEach.call(roots, function (root) {
      instances.push(new ScrollSpin(root));
    });
  }

  function destroy(scope) {
    instances = instances.filter(function (inst) {
      if (!scope || scope.contains(inst.root)) {
        inst.destroy();
        return false;
      }
      return true;
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  // Shopify theme editor: sections get swapped in and out of the DOM live.
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
  document.addEventListener('shopify:section:unload', function (e) { destroy(e.target); });

  window.ScrollSpin = { init: init, destroy: destroy, instances: instances };
})();